import React, { useEffect, useState } from 'react';
import { Mail, ShieldCheck, Wallet } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../services/api';
import { describePayRate, setPayRates } from '../../utils/payRates';
import { Button, DetailItem, DetailList, DetailSection, Input, Modal, useToast } from '../ui';

const roleLabels: Record<string, string> = {
    ADMIN: 'Quản trị viên',
    MANAGER: 'Quản lý',
    TEACHER: 'Giáo viên',
    ASSISTANT: 'Trợ giảng',
    STAFF: 'Nhân viên',
};

interface ProfileDialogProps {
    open: boolean;
    onClose: () => void;
}

// The signed-in user's own details. Name and phone are editable; email, role and pay rates are set by admins.
export default function ProfileDialog({ open, onClose }: ProfileDialogProps) {
    const { user, updateUser } = useAuth();
    const { showToast } = useToast();
    const [form, setForm] = useState({ firstName: '', lastName: '', phone: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '', phone: user?.phone ?? '' });
        // Refresh so rates changed by an admin show up without logging in again.
        authApi.getMe()
            .then((response: any) => {
                const fresh = response.data.data.user;
                updateUser({ ...user!, ...fresh });
                setForm({ firstName: fresh.firstName ?? '', lastName: fresh.lastName ?? '', phone: fresh.phone ?? '' });
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!user) return null;

    const dirty = form.firstName.trim() !== user.firstName
        || form.lastName.trim() !== user.lastName
        || form.phone.trim() !== (user.phone ?? '');

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!form.firstName.trim() || !form.lastName.trim()) {
            showToast('Vui lòng nhập đủ họ và tên.', 'error');
            return;
        }
        setSaving(true);
        try {
            const response: any = await authApi.updateMe({
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                phone: form.phone.trim() || null,
            });
            updateUser({ ...user, ...response.data.data.user });
            showToast('Đã cập nhật thông tin cá nhân.', 'success');
            onClose();
        } catch (error: any) {
            showToast(error?.message || 'Cập nhật thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const payRates = setPayRates(user.payRates);

    return (
        <Modal
            open={open}
            title="Thông tin cá nhân"
            onClose={() => !saving && onClose()}
            className="lg:max-w-2xl"
            footer={(
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Đóng</Button>
                    <Button type="submit" form="profile-form" loading={saving} disabled={!dirty}>Lưu thay đổi</Button>
                </div>
            )}
        >
            <form id="profile-form" onSubmit={submit} className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                        label="Họ"
                        value={form.firstName}
                        onChange={event => setForm({ ...form, firstName: event.target.value })}
                        required
                        disabled={saving}
                    />
                    <Input
                        label="Tên"
                        value={form.lastName}
                        onChange={event => setForm({ ...form, lastName: event.target.value })}
                        required
                        disabled={saving}
                    />
                    <Input
                        label="Số điện thoại"
                        type="tel"
                        value={form.phone}
                        onChange={event => setForm({ ...form, phone: event.target.value })}
                        disabled={saving}
                        className="sm:col-span-2"
                    />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                    <DetailSection title="Tài khoản">
                        <DetailList>
                            <DetailItem icon={<Mail />} label="Email" value={user.email} />
                            <DetailItem icon={<ShieldCheck />} label="Vai trò" value={roleLabels[user.role] ?? user.role} />
                        </DetailList>
                    </DetailSection>
                    <DetailSection title="Lương theo ca">
                        <DetailList>
                            {payRates.length === 0 ? (
                                <DetailItem icon={<Wallet />} label="Mức lương" value="" fallback="Chưa cài" />
                            ) : payRates.map(item => (
                                <DetailItem key={item.shiftTypeId} icon={<Wallet />} label={item.name} value={describePayRate(item)} />
                            ))}
                        </DetailList>
                    </DetailSection>
                </div>
                <p className="text-xs text-ink-500">Email, vai trò và mức lương do admin cập nhật.</p>
            </form>
        </Modal>
    );
}
